import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Loader2, Zap, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

export const PLANS = [
  {
    id: "startup", name: "Developer / Startup", tag: "For teams up to 20",
    monthly: 299, yearly: 2870, lookup: { month: "startup_monthly", year: "startup_yearly" },
    features: ["1 AI Security Engineer", "Weekly continuous SAST/DAST", "SOC 2 basic readiness",
      "Automated GitHub PR fixes", "Email & Slack alerts"],
  },
  {
    id: "growth", name: "Growth Pro", tag: "Most popular for scale-ups", popular: true,
    monthly: 799, yearly: 7670, lookup: { month: "growth_monthly", year: "growth_yearly" },
    features: ["3 autonomous AI Security Engineers", "24/7 continuous cloud & code scanning",
      "Full SOC 2, ISO 27001, GDPR & HIPAA readiness", "Auto-remediation with human-in-the-loop",
      "1-on-1 CISO office hours"],
  },
  {
    id: "enterprise", name: "Enterprise Shield", tag: "Regulated & enterprise scale", contact: true,
    monthly: null, features: ["Dedicated AI agent cluster", "Custom compliance frameworks",
      "On-prem / private cloud scanners", "Unlimited repos & cloud accounts", "Dedicated engineer SLA"],
  },
];

export function PricingCards({ compact = false }) {
  const [yearly, setYearly] = useState(false);
  const [loading, setLoading] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  const checkout = async (plan) => {
    if (plan.contact) {
      toast.info("Our team will reach out — email sales@aicc-agents.com");
      return;
    }
    if (!user) {
      navigate("/signup");
      return;
    }
    setLoading(plan.id);
    try {
      const lookup_key = plan.lookup[yearly ? "year" : "month"];
      const { data } = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setLoading(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-center gap-3 mb-10">
        <span className={`text-sm font-medium ${!yearly ? "text-ink" : "text-muted"}`}>Monthly</span>
        <Switch checked={yearly} onCheckedChange={setYearly} data-testid="billing-toggle" />
        <span className={`text-sm font-medium ${yearly ? "text-ink" : "text-muted"}`}>
          Yearly <span className="text-secure text-xs">· save ~20%</span>
        </span>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
        {PLANS.map((plan) => {
          const price = plan.contact ? null : yearly ? plan.yearly : plan.monthly;
          return (
            <div key={plan.id} data-testid={`plan-${plan.id}`}
              className={`relative card-surface rounded-2xl p-7 flex flex-col ${plan.popular ? "border-cyber/50 glow-cyan" : ""}`}>
              {plan.popular && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-cyber text-[#0B0E14] font-semibold border-0">
                  <Zap size={12} className="mr-1" /> Most Popular
                </Badge>
              )}
              <h3 className="font-display font-bold text-xl text-ink">{plan.name}</h3>
              <p className="text-sm text-muted mt-1">{plan.tag}</p>
              <div className="mt-5 flex items-end gap-1.5">
                {plan.contact ? (
                  <span className="font-display font-extrabold text-3xl text-ink">Custom</span>
                ) : (
                  <>
                    <span className="font-display font-extrabold text-4xl text-ink">${price.toLocaleString()}</span>
                    <span className="text-muted text-sm mb-1.5">/{yearly ? "yr" : "mo"}</span>
                  </>
                )}
              </div>
              <Button
                onClick={() => checkout(plan)}
                disabled={loading === plan.id}
                data-testid={`plan-cta-${plan.id}`}
                className={`mt-6 h-11 font-semibold ${plan.popular ? "bg-cyber text-[#0B0E14] hover:bg-cyber-dim" : "bg-[var(--surface-2)] text-ink border border-line hover:border-cyber"}`}
              >
                {loading === plan.id ? <Loader2 size={18} className="animate-spin" /> :
                  plan.contact ? "Contact Sales" : (<>Get {plan.name.split(" ")[0]} <ArrowRight size={16} className="ml-1.5" /></>)}
              </Button>
              <ul className="mt-7 space-y-3 flex-1">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-muted">
                    <Check size={17} className="text-secure shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
      <p className="text-center text-xs text-muted mt-8 font-mono">
        Test mode · use card 4242 4242 4242 4242 · any future expiry · any CVC
      </p>
    </div>
  );
}

export default function PricingPage() {
  return (
    <div className="min-h-screen bg-bg text-ink">
      <Navbar />
      <section className="relative overflow-hidden border-b border-line">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="absolute inset-0 radial-fade" />
        <div className="relative max-w-7xl mx-auto px-4 lg:px-8 py-16 lg:py-20 text-center">
          <p className="text-cyber font-mono text-xs tracking-widest uppercase mb-3">Pricing</p>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">
            Security capability without <span className="text-cyber">enterprise overhead</span>
          </h1>
          <p className="mt-4 text-muted max-w-2xl mx-auto">
            Deploy autonomous AI Security Engineers that continuously turn findings into verifiable outcomes.
          </p>
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-16">
        <PricingCards />
      </section>
      <Footer />
    </div>
  );
}
